<template>
  <div class="file-management">
    <!-- 服务控制卡片 - 合并到一行 -->
    <div class="services-row">
      <!-- Web文件服务 -->
      <div class="service-card">
        <h3>{{ $t('file.fileManagement.webFileService') }}</h3>
        <div class="service-controls">
          <span class="status-badge" :class="{ 'running': fileSwitch }">
            {{ fileSwitch ? $t('file.fileManagement.running') : $t('file.fileManagement.stopped') }}
          </span>
          <button
            @click="toggleWebService"
            :disabled="webServiceLoading"
            class="action-btn"
          >
            <span v-if="webServiceLoading">{{ $t('file.fileManagement.processing') }}</span>
            <span v-else>{{ fileSwitch ? $t('file.fileManagement.stop') : $t('file.fileManagement.start') }}</span>
          </button>
        </div>
      </div>

      <!-- 网络文件服务 -->
      <div class="service-card clickable" @click="$router.push('/network-file-services')">
        <h3>{{ $t('file.fileManagement.networkFileService') }}</h3>
        <p class="service-hint">WebDAV、Samba、SFTP</p>
      </div>

      <!-- 在线文件编辑设置 -->
      <div class="service-card clickable" @click="showOnlineEditDialog = true">
        <h3>{{ $t('file.fileManagement.onlineEditSetting') }}</h3>
        <p class="service-hint">{{ $t('file.fileManagement.onlineEditHint') }}</p>
      </div>
    </div>

    <!-- 在线文件编辑设置弹窗 -->
    <el-dialog
      v-model="showOnlineEditDialog"
      :title="$t('file.fileManagement.onlineEditSetting')"
      width="800px"
      :close-on-click-modal="false"
    >
      <div class="online-edit-content">
        <!-- 第一行：Office地址和IP设置 -->
        <div class="table-responsive">
          <table class="settings-table">
            <thead>
              <tr>
                <th>{{ $t('file.fileManagement.officeAddress') }}</th>
                <th>{{ $t('file.fileManagement.officeIp') }}</th>
                <th>{{ $t('file.fileManagement.operation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td :data-label="$t('file.fileManagement.officeAddress')">
                  <el-input
                    v-model="officeSettings.officeUrl"
                    size="small"
                    :placeholder="$t('file.fileManagement.officeUrlPlaceholder')"
                    :disabled="onlineEdit"
                  />
                </td>
                <td :data-label="$t('file.fileManagement.officeIp')">
                  <el-input
                    v-model="officeSettings.officeAccessIP"
                    size="small"
                    :disabled="onlineEdit"
                  />
                </td>
                <td :data-label="$t('file.fileManagement.operation')" class="action-cell">
                  <div class="action-buttons">
                    <el-button
                      v-if="!onlineEdit"
                      type="primary"
                      size="small"
                      @click="updateFileSetting('on')"
                      :loading="fileSettingLoading"
                    >
                      {{ $t('file.fileManagement.enable') }}
                    </el-button>
                    <el-button
                      type="primary"
                      size="small"
                      @click="updateFileSetting('off')"
                      :loading="fileSettingLoading"
                    >
                      {{ !onlineEdit ? $t('file.fileManagement.update') : $t('file.fileManagement.close') }}
                    </el-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 第二行：文件扩展名设置 -->
        <div class="table-responsive" style="margin-top: 20px;">
          <table class="settings-table">
            <thead>
              <tr>
                <th>{{ $t('file.fileManagement.editableExtensions') }}</th>
                <th>{{ $t('file.fileManagement.readonlyExtensions') }}</th>
                <th>{{ $t('file.fileManagement.operation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td :data-label="$t('file.fileManagement.editableExtensions')">
                  <el-select
                    v-model="officeSettings.officeWriteSelected"
                    multiple
                    filterable
                    allow-create
                    size="small"
                    class="full-width"
                    :placeholder="$t('file.fileManagement.selectOrInputExtensions')"
                  >
                    <el-option
                      v-for="ext in defaultExtensions"
                      :key="'write-'+ext"
                      :label="ext"
                      :value="ext"
                    />
                  </el-select>
                </td>
                <td :data-label="$t('file.fileManagement.readonlyExtensions')">
                  <el-select
                    v-model="officeSettings.officeReadSelected"
                    multiple
                    filterable
                    allow-create
                    size="small"
                    class="full-width"
                    :placeholder="$t('file.fileManagement.selectOrInputExtensions')"
                  >
                    <el-option
                      v-for="ext in defaultExtensions"
                      :key="'read-'+ext"
                      :label="ext"
                      :value="ext"
                    />
                  </el-select>
                </td>
                <td :data-label="$t('file.fileManagement.operation')" class="action-cell">
                  <el-button
                    type="primary"
                    size="small"
                    @click="updateOfficeAttr"
                    :loading="officeAttrLoading"
                  >
                    {{ $t('file.fileManagement.update') }}
                  </el-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <template #footer>
        <el-button @click="showOnlineEditDialog = false">{{ $t('file.fileManagement.close') }}</el-button>
      </template>
    </el-dialog>

    <!-- 目录列表 -->
    <div class="directory-list">
      <div class="list-header">
        <h3>{{ $t('file.fileManagement.directoryList') }}</h3>
        <div class="header-actions">
          <button @click="showModuleLogDialog" class="log-btn">
            <span class="log-icon">📋</span> {{ $t('file.fileManagement.viewLogs') }}
          </button>
          <button @click="fetchData" class="refresh-btn">{{ $t('file.fileManagement.refreshData') }}</button>
        </div>
      </div>

      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>{{ $t('file.fileManagement.username') }}</th>
              <th>{{ $t('file.fileManagement.status') }}</th>
              <th>{{ $t('file.fileManagement.directory') }}</th>
              <th>{{ $t('file.fileManagement.readonly') }}</th>
              <th>{{ $t('file.fileManagement.delete') }}</th>
              <th>{{ $t('file.fileManagement.space') }}</th>
              <th>{{ $t('file.fileManagement.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(item, index) in userList" :key="index">
              <!-- 用户行 -->
              <tr class="user-row">
                <td data-label="username">{{ item.username }}</td>
                <td data-label="status">
                  <span class="status-badge" :class="{ 'active': item.localFileSwitch }">
                    {{ item.localFileSwitch ? $t('file.fileManagement.activated') : $t('file.fileManagement.notActivated') }}
                  </span>
                </td>
                <td data-label="directory" colspan="1">
                  <button class="add-directory-btn" @click="openDirectoryDialog(item)">{{ $t('file.fileManagement.addDirectory') }}</button>
                </td>
                <td data-label="readonly"></td>
                <td data-label="delete"></td>
                <td data-label="space"></td>
                <td data-label="actions" class="actions">
                  <div class="action-buttons">
                    <button class="action-btn save-btn" @click="saveUserConfig(item)">{{ $t('file.fileManagement.save') }}</button>
                    <button
                      v-if="item.localFileSwitch"
                      @click="stopUserService(item)"
                      class="action-btn deactivate"
                    >
                      {{ $t('file.fileManagement.deactivate') }}
                    </button>
                    <button
                      v-else
                      @click="saveUserConfig(item)"
                      class="action-btn activate"
                    >
                      {{ $t('file.fileManagement.activate') }}
                    </button>
                  </div>
                </td>
              </tr>
              <!-- 每个目录的行 -->
              <tr v-for="(dir, dirIndex) in item.localDir" :key="dirIndex" class="directory-row">
                <td data-label="username"></td>
                <td data-label="status"></td>
                <td data-label="directory">
                  <div class="directory-item">
                    <span class="directory-path">{{ dir.dir }}</span>
                    <button @click="removeDirectory(item, dirIndex)" class="remove-btn" :title="$t('file.fileManagement.removeDirectory')">×</button>
                  </div>
                </td>
                <td data-label="readonly">
                  <select v-model="dir.readOnly" class="select-dropdown">
                    <option :value="true">{{ $t('file.fileManagement.yes') }}</option>
                    <option :value="false">{{ $t('file.fileManagement.no') }}</option>
                  </select>
                </td>
                <td data-label="delete">
                  <select v-model="dir.noDelete" class="select-dropdown">
                    <option :value="true">{{ $t('file.fileManagement.forbidden') }}</option>
                    <option :value="false">{{ $t('file.fileManagement.allowed') }}</option>
                  </select>
                </td>
                <td data-label="space">
                  <span class="quota" :class="{ 'pending-text': dir.usage === $t('file.fileManagement.clickToApply') }">{{ dir.usage === '请点击保存后生效' ? $t('file.fileManagement.clickToApply') : dir.usage }}</span>
                </td>
                <td data-label="操作"></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 目录选择对话框 -->
    <div v-if="showDirectoryDialog" class="dialog-overlay" @click.self="cancelDirectorySelection">
      <div class="directory-dialog">
        <div class="dialog-header">
          <button
            v-if="currentPathInfo.notTopDir"
            @click="goToPrevDirectory"
            class="nav-btn prev-btn"
          >
            ↑ {{ $t('file.fileManagement.parentFolder') }}
          </button>
          <h3 class="current-path">{{ $t('file.filePath') }}: {{ currentPathInfo.currentPath || '/' }}</h3>
        </div>
        
        <!-- 新增手动输入区域 -->
        <div class="manual-input-section">
          <div class="input-header">
            <span class="input-label">{{ $t('file.fileManagement.directory') }}:</span>
          </div>
          <div class="input-controls">
            <input
              type="text"
              v-model="manualDirectoryPath"
              :placeholder="$t('file.fileManagement.directory')"
              class="directory-input"
              @keyup.enter="confirmManualDirectory"
            />
            <button
              @click="confirmManualDirectory"
              class="dialog-btn confirm manual-btn"
              :disabled="!manualDirectoryPath"
            >
              {{ $t('common.confirm') }}
            </button>
          </div>
        </div>

        <div class="dialog-content">
          <div v-if="loadingDirectories" class="loading-indicator">
            {{ $t('common.loading') }}
          </div>
          <div v-else class="directory-tree">
            <ul v-if="directories.length > 0">
              <li 
                v-for="dir in directories" 
                :key="dir.Path"
                @click="selectDirectory(dir)"
                @dblclick="enterDirectory(dir)"
                class="directory-entry"
                :class="{ 'selected': selectedDirectory === dir.Path }"
              >
                <span class="folder-icon">📁</span>
                <span class="directory-name">{{ dir.Name }}</span>
                <span class="enter-icon" @click.stop="enterDirectory(dir)" :title="$t('common.next')">→</span>
              </li>
            </ul>
            <div v-else class="empty-directory">
              {{ $t('file.noFiles') }}
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <div class="selected-path" v-if="selectedDirectory">
            {{ $t('common.select') }}: {{ selectedDirectory }}
          </div>
          <div class="action-buttons">
            <button @click="cancelDirectorySelection" class="dialog-btn cancel">{{ $t('common.cancel') }}</button>
            <button
              @click="confirmDirectorySelection"
              class="dialog-btn confirm"
              :disabled="!selectedDirectory"
            >
              {{ $t('common.confirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 文件管理模块日志查看对话框 -->
    <el-dialog
      v-model="moduleLogDialogVisible"
      :title="$t('log.title')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('log.logTotal', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('log.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">(5{{ $t('log.second') }})</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('log.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshModuleLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('common.refresh') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('log.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('file.fileManagement.noLogRecords') }}</p>
        </div>
        <div v-else class="log-list">
          <div 
            v-for="(log, index) in logsData" 
            :key="index" 
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="moduleLogDialogVisible = false">{{ $t('file.fileManagement.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  name: 'FileManagement',
  components: {
    // 注意：这里不需要注册图标，因为直接在模板中使用
  },
  data() {
    return {
      fileSwitch: false,
      webServiceLoading: false,

      showDirectoryDialog: false,

      // 在线文件编辑设置相关状态
      onlineEdit: false,
      officeSettings: {
        officeUrl: '',
        officeAccessIP: '',
        officeWriteSelected: [],
        officeReadSelected: []
      },
      defaultExtensions: ['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'],
      fileSettingLoading: false,
      officeAttrLoading: false,
      showOnlineEditDialog: false,
      loadingDirectories: false,
      currentEditingItem: null,
      selectedDirectory: '',
      directories: [],
      currentPathInfo: {
        currentPath: '/',
        prevPath: '',
        notTopDir: false
      },
      manualDirectoryPath: '',
      userList: [],
      isMobile: window.innerWidth < 768,
      
      // 模块日志相关
      moduleLogDialogVisible: false,
      logLoading: false,
      logClearing: false,
      logDownloading: false,
      logsData: [],
      logTotal: 0,
      logCurrentPage: 1,
      logPageSize: 50,
      logContentRef: null,
      autoRefreshEnabled: true,
      logRefreshInterval: null
    };
  },
  created() {
    this.fetchData();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.stopLogAutoRefresh();
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth < 768;
    },
    
    // 检查登录状态
    checkLogin(data) {
      if (data?.errCode === 1000) {
        ElMessage.error(this.$t('file.fileManagement.pleaseLoginFirst'));
        this.$router.push('/user-login?callback=file-setting');
        return false;
      }
      return true;
    },
    
    // 显示模块日志对话框
    showModuleLogDialog() {
      this.moduleLogDialogVisible = true;
      this.fetchModuleLogs();
      if (this.autoRefreshEnabled) {
        this.startLogAutoRefresh();
      }
    },
    
    // 切换自动刷新
    toggleAutoRefresh(enabled) {
      if (enabled) {
        this.startLogAutoRefresh();
        ElMessage.success(this.$t('file.fileManagement.autoRefreshEnabled'));
      } else {
        this.stopLogAutoRefresh();
        ElMessage.info(this.$t('file.fileManagement.autoRefreshDisabled'));
      }
    },
    
    // 启动日志自动刷新
    startLogAutoRefresh() {
      this.stopLogAutoRefresh();
      this.logRefreshInterval = setInterval(() => {
        if (this.moduleLogDialogVisible) {
          this.fetchModuleLogs(false);
        }
      }, 5000);
    },
    
    // 停止日志自动刷新
    stopLogAutoRefresh() {
      if (this.logRefreshInterval) {
        clearInterval(this.logRefreshInterval);
        this.logRefreshInterval = null;
      }
    },
    
    // 获取模块日志
    async fetchModuleLogs(showLoading = true) {
      try {
        if (showLoading) {
          this.logLoading = true;
        }
        
        const params = new URLSearchParams({
          module: 'file',
          page: this.logCurrentPage,
          size: this.logPageSize
        });
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });

        if (!response.ok) throw new Error(this.$t('file.fileManagement.networkError'));

        const data = await response.json();
        if (!this.checkLogin(data)) return;

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('file.fileManagement.fetchLogsFailed'));
        }

        this.logsData = data.data || [];
        this.logTotal = data.count || 0;

        if (showLoading && this.$refs.logContentRef) {
          this.$refs.logContentRef.scrollTop = 0;
        }
      } catch (error) {
        console.error(this.$t('file.fileManagement.fetchLogsFailed'), error);
        if (showLoading) {
          ElMessage.error(this.$t('file.fileManagement.fetchLogsError', { error: error.message || this.$t('file.fileManagement.unknownError') }));
        }
      } finally {
        if (showLoading) {
          this.logLoading = false;
        }
      }
    },
    
    // 刷新模块日志
    refreshModuleLogs() {
      this.fetchModuleLogs(true);
    },
    
    // 清空日志
    async clearLogs() {
      try {
        await ElMessageBox.confirm(
          this.$t('file.fileManagement.confirmClearLogs'),
          this.$t('common.prompt'),
          {
            confirmButtonText: this.$t('common.confirm'),
            cancelButtonText: this.$t('common.cancel'),
            type: 'warning'
          }
        );

        this.logClearing = true;

        const params = new URLSearchParams({
          module: 'file'
        });

        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (!response.ok) throw new Error(this.$t('file.fileManagement.networkError'));

        const data = await response.json();
        if (!this.checkLogin(data)) return;

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('file.fileManagement.clearLogsFailed'));
        }

        ElMessage.success(this.$t('file.fileManagement.logsCleared'));
        this.fetchModuleLogs(true);
      } catch (error) {
        if (error !== 'cancel') {
          console.error(this.$t('file.fileManagement.clearLogsFailed'), error);
          ElMessage.error(this.$t('file.fileManagement.fetchLogsError', { error: error.message || this.$t('file.fileManagement.unknownError') }));
        }
      } finally {
        this.logClearing = false;
      }
    },
    
    // 下载所有日志
    async downloadAllLogs() {
      try {
        this.logDownloading = true;

        const params = new URLSearchParams({
          module: 'file',
          page: 1,
          size: 10000
        });

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });

        if (!response.ok) throw new Error(this.$t('file.fileManagement.networkError'));

        const data = await response.json();
        if (!this.checkLogin(data)) return;

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('file.fileManagement.fetchLogsFailed'));
        }

        const allLogs = data.data || [];

        if (allLogs.length === 0) {
          ElMessage.warning(this.$t('file.fileManagement.noLogsToDownload'));
          return;
        }

        let logContent = '';
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`;
        });

        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `file_mgmt_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        ElMessage.success(this.$t('file.fileManagement.downloadLogsSuccess', { count: allLogs.length }));
      } catch (error) {
        console.error(this.$t('file.fileManagement.downloadLogsFailed', { error: '' }), error);
        ElMessage.error(this.$t('file.fileManagement.downloadLogsFailed', { error: error.message || this.$t('file.fileManagement.unknownError') }));
      } finally {
        this.logDownloading = false;
      }
    },
    
    // 处理每页条数变化
    handleLogSizeChange(size) {
      this.logPageSize = size;
      this.logCurrentPage = 1;
      this.fetchModuleLogs(true);
    },
    
    // 处理页码变化
    handleLogPageChange(page) {
      this.logCurrentPage = page;
      this.fetchModuleLogs(true);
    },
    
    async fetchData() {
      try {
        const response = await fetch(`${API_BASE_URL}/list-fileMgmtUser`,
          {
            method: 'GET',
            credentials: 'include'
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.errMsg || this.$t('file.fileManagement.fetchDataFailed'));
        }

        if (data.errCode === 1000) {
          ElMessage.error(this.$t('file.fileManagement.pleaseLoginFirst'));
          this.$router.push('/user-login?callback=file-setting');
          return;
        }

        if (data.errCode != 0) {
          ElMessage.error(data.errMsg)
          return
        }

        if (data.errCode === 0) {
          this.fileSwitch = data.fileSwitch;
          this.userList = data.user.map(user => ({
            ...user,
            active: true,
            localDir: user.localDir || []
          }));
        } else {
          throw new Error(data.errMsg || this.$t('file.fileManagement.fetchDataFailed'));
        }
        this.fetchSettings();
      } catch (error) {
        console.error(this.$t('file.fileManagement.fetchDataFailed'), error);
        ElMessage.error(error.message);
      }
    },

    // 获取在线文件编辑设置
    async fetchSettings() {
      try {
        const response = await fetch(`${API_BASE_URL}/get-settingInfo`, {
          credentials: 'include'
        });
        const data = await response.json();

        if (data.errCode === 1000) {
          ElMessage.warning(this.$t('file.fileManagement.pleaseLogin'));
          this.$router.push('/user-login?callback=file-setting');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('file.fileManagement.fetchSettingsFailed'));
        }

        // 更新在线office设置
        this.onlineEdit = data.onlineEdit;
        this.officeSettings.officeUrl = data.officeUrl;
        this.officeSettings.officeAccessIP = data.officeAccessIP;
        this.officeSettings.officeWriteSelected = data.officeWrite
          ? data.officeWrite.split(',').filter(item => item.trim() !== '')
          : [],
        this.officeSettings.officeReadSelected = data.officeRead
          ? data.officeRead.split(',').filter(item => item.trim() !== '')
          : [];
      } catch (error) {
        console.error(this.$t('file.fileManagement.fetchSettingsFailed'), error);
        ElMessage.error(error.message || this.$t('file.fileManagement.fetchSettingsFailed'));
      }
    },

    // 更新文件设置（开启/关闭）
    async updateFileSetting(flag) {
      try {
        this.fileSettingLoading.value = true;

        const params = new URLSearchParams();
        params.append('officeUrl', this.officeSettings.officeUrl);
        params.append('officeAccessIP', this.officeSettings.officeAccessIP);
        params.append('onlineEdit', flag);

        const response = await fetch(`${API_BASE_URL}/update-fileSetting`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        });

        const data = await response.json();

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('file.fileManagement.updateFileSettingFailed'));
        }

        ElMessage.success(data.errMsg || this.$t('file.fileManagement.fileSettingUpdated'));
        this.onlineEdit = flag === 'on';
      } catch (error) {
        console.error(this.$t('file.fileManagement.updateFileSettingFailed'), error);
        ElMessage.error(error.message || this.$t('file.fileManagement.updateFileSettingFailed'));
      } finally {
        this.fileSettingLoading = false;
      }
    },

    // 更新文件属性（扩展名设置）
    async updateOfficeAttr() {
      try {
        this.officeAttrLoading = true;

        // 验证至少有一个扩展名
        if (this.officeSettings.officeWriteSelected.length === 0 &&
            this.officeSettings.officeReadSelected.length === 0) {
          ElMessage.warning(this.$t('file.fileManagement.extensionsCannotBeEmpty'));
          return;
        }

        const params = new URLSearchParams();
        params.append('officeWrite', this.officeSettings.officeWriteSelected.join(','));
        params.append('officeRead', this.officeSettings.officeReadSelected.join(','));

        const response = await fetch(`${API_BASE_URL}/update-officeAttr`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        });

        const data = await response.json();

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('file.fileManagement.updateOfficeAttrFailed'));
        }

        ElMessage.success(this.$t('file.fileManagement.officeAttrUpdated'));
      } catch (error) {
        console.error(this.$t('file.fileManagement.updateOfficeAttrFailed'), error);
        ElMessage.error(error.message || this.$t('file.fileManagement.updateOfficeAttrFailed'));
      } finally {
        this.officeAttrLoading = false;
      }
    },

    async toggleWebService() {
      this.webServiceLoading = true;
      try {
        const url = this.fileSwitch
          ? `${API_BASE_URL}/stop-localFile`
          : `${API_BASE_URL}/start-localFile`;

        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.errMsg || this.$t('file.fileManagement.operationFailed'));
        }

        if (data.errCode === 1000) {
          ElMessage.error(this.$t('file.fileManagement.pleaseLoginFirst'));
          this.$router.push('/user-login?callback=file-setting');
          return;
        }

        if (data.errCode === 0) {
          this.fileSwitch = !this.fileSwitch;
          ElMessage.success(this.fileSwitch ? this.$t('file.fileManagement.webServiceStarted') : this.$t('file.fileManagement.webServiceStopped'));
          this.fetchData();
        } else {
          throw new Error(data.errMsg || this.$t('file.fileManagement.operationFailed'));
        }
      } catch (error) {
        console.error(this.$t('file.fileManagement.operationFailed'), error);
        ElMessage.error(this.$t('file.fileManagement.operationFailed') + ': ' + error.message);
      } finally {
        this.webServiceLoading = false;
      }
    },

    async stopUserService(item) {
      try {
        const formData = new URLSearchParams();
        formData.append('username', item.username);

        const response = await fetch(`${API_BASE_URL}/stop-fileMgmtUser`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: formData
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.errMsg || this.$t('file.fileManagement.stopUserFailed'));
        }

        if (data.errCode === 1000) {
          ElMessage.error(this.$t('file.fileManagement.pleaseLoginFirst'));
          this.$router.push('/user-login?callback=file-setting');
          return;
        }

        if (data.errCode !== 0) {
          ElMessage.error(data.errMsg);
          return;
        }

        if (data.errCode === 0) {
          item.active = false;
          ElMessage.success(this.$t('file.fileManagement.userStopped', { username: item.username }));
          this.fetchData();
        } else {
          throw new Error(data.errMsg || this.$t('file.fileManagement.stopUserFailed'));
        }
      } catch (error) {
        console.error(this.$t('file.fileManagement.stopUserFailed'), error);
        ElMessage.error(this.$t('file.fileManagement.stopUserFailed') + ': ' + error.message);
      }
    },
    async saveUserConfig(item) {
      try {
        if (!item.localDir || item.localDir.length === 0) {
          ElMessage.warning(this.$t('file.fileManagement.pleaseAddDirectoryFirst'));
          return;
        }

        const dirAttr = item.localDir.map(dir => ({
          dirContent: dir.dir,
          readOnlyStr: dir.readOnly ? "yes" : "no",
          noDeleteStr: dir.noDelete ? "yes" : "no"
        }));

        const formData = new URLSearchParams();
        formData.append('username', item.username);
        formData.append('dirAttr', JSON.stringify(dirAttr));

        const response = await fetch(`${API_BASE_URL}/update-fileMgmtUser`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: formData
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.errMsg || this.$t('file.fileManagement.saveFailed'));
        }

        if (data.errCode === 1000) {
          ElMessage.error(this.$t('file.fileManagement.pleaseLoginFirst'));
          this.$router.push('/user-login?callback=file-setting');
          return;
        }

        if (data.errCode === 0) {
          ElMessage.success(this.$t('file.fileManagement.configSaveSuccess'));
          this.fetchData();
        } else {
          throw new Error(data.errMsg || this.$t('file.fileManagement.saveFailed'));
        }
      } catch (error) {
        console.error(this.$t('file.fileManagement.saveFailed'), error);
        ElMessage.error(this.$t('file.fileManagement.saveConfigError', { error: error.message }));
      }
    },
    async openDirectoryDialog(item) {
      this.currentEditingItem = item;
      this.selectedDirectory = '';
      this.showDirectoryDialog = true;
      this.currentPathInfo = {
        currentPath: '/',
        prevPath: '',
        notTopDir: false
      };
      await this.fetchDirectories('/');
    },
    removeDirectory(item, index) {
      const removedPath = item.localDir[index].dir;
      item.localDir.splice(index, 1);
      ElMessage.success(this.$t('file.fileManagement.directoryRemoved', { path: removedPath }));
    },
    async fetchDirectories(path) {
      this.loadingDirectories = true;
      try {
        const response = await fetch(`${API_BASE_URL}/get-dirList?path=${encodeURIComponent(path)}`,
          {
            method: 'GET',
            credentials: 'include'
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.errMsg || this.$t('file.fileManagement.fetchDirectoryListFailed'));
        }

        if (data.errCode === 1000) {
          ElMessage.error(this.$t('file.fileManagement.pleaseLoginFirst'));
          this.showDirectoryDialog = false;
          this.$router.push('/user-login?callback=file-setting');
          return;
        }

        if (data.errCode === 0) {
          this.directories = data.file || [];
          this.currentPathInfo = {
            currentPath: path,
            prevPath: data.prevPath,
            notTopDir: data.notTopDir
          };
        } else {
          throw new Error(data.errMsg || this.$t('file.fileManagement.fetchDirectoryListFailed'));
        }
      } catch (error) {
        console.error(this.$t('file.fileManagement.fetchDirectoryListFailed'), error);
        ElMessage.error(error.message);
        this.directories = [];
      } finally {
        this.loadingDirectories = false;
      }
    },
    async goToPrevDirectory() {
      if (this.currentPathInfo.notTopDir && this.currentPathInfo.prevPath) {
        await this.fetchDirectories(this.currentPathInfo.prevPath);
        this.selectedDirectory = '';
      }
    },
    selectDirectory(dir) {
      this.selectedDirectory = dir.Path;
    },
    enterDirectory(dir) {
      this.fetchDirectories(dir.Path);
      this.selectedDirectory = '';
    },

    confirmManualDirectory() {
      if (!this.manualDirectoryPath.trim()) {
        ElMessage.warning(this.$t('file.fileManagement.pleaseInputDirectoryPath'));
        return;
      }

      const path = this.manualDirectoryPath.trim();
      this.selectedDirectory = path;
      ElMessage.success(this.$t('file.fileManagement.directorySelected', { path: path }));
      this.manualDirectoryPath = '';
    },

    confirmDirectorySelection() {
      if (this.currentEditingItem) {
        if (!this.currentEditingItem.localDir) {
          this.currentEditingItem.localDir = [];
        }

        if (!this.selectedDirectory) {
          ElMessage.warning(this.$t('file.fileManagement.pleaseSelectDirectory'));
          return;
        }

        const exists = this.currentEditingItem.localDir.some(
          d => d.dir === this.selectedDirectory
        );

        if (!exists) {
          this.currentEditingItem.localDir.push({
            dir: this.selectedDirectory,
            fileName: this.selectedDirectory,
            readOnly: false,
            noDelete: false,
            usage: this.$t('file.fileManagement.clickToApply')
          });
          ElMessage.success(this.$t('file.fileManagement.directoryAdded', { path: this.selectedDirectory }));
        } else {
          ElMessage.warning(this.$t('file.fileManagement.directoryAlreadyExists'));
        }
      }
      this.showDirectoryDialog = false;
    },
    cancelDirectorySelection() {
      this.showDirectoryDialog = false;
    }
  }
};
</script>

<style scoped>
.file-management {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  min-height: 100vh;
  background-color: #f5f7fa;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .file-management {
    padding: 12px;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .file-management {
    padding: 8px;
  }
}

.user-link {
  text-decoration: none;
  color: #000000;
  font-weight: 600;
  font-size: 1rem;
  letter-spacing: 1px;
  transition: color 0.3s;
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
}

.user-link:hover {
  color: #337ecc;
  text-decoration: none;
}

.services-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  width: 100%;
}

@media (max-width: 768px) {
  .services-row {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
  }
}

.service-card {
  flex: 1;
  background: white;
  border-radius: 8px;
  padding: 15px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.service-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.service-card h3 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

@media (max-width: 768px) {
  .service-card {
    padding: 12px 16px;
    width: 100%;
  }
  
  .service-card h3 {
    font-size: 15px;
    margin-bottom: 8px;
  }
}

@media (max-width: 480px) {
  .service-card {
    padding: 10px 12px;
  }
  
  .service-card h3 {
    font-size: 14px;
  }
}

.service-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .service-controls {
    gap: 8px;
  }
}

.status-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.status-badge.running,
.status-badge.active {
  background: #e6f7e6;
  color: #4caf50;
}

.status-badge:not(.running):not(.active) {
  background: #f0f0f0;
  color: #757575;
}

@media (max-width: 768px) {
  .status-badge {
    padding: 3px 8px;
    font-size: 11px;
  }
}

.service-controls .action-btn {
  padding: 6px 12px;
  background: #f0f0f0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s;
  min-width: 60px;
}

.service-controls .action-btn:hover {
  background: #e0e0e0;
}

.service-controls .action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .service-controls .action-btn {
    padding: 5px 10px;
    font-size: 12px;
    min-width: 50px;
  }
}

@media (max-width: 480px) {
  .service-controls .action-btn {
    padding: 4px 8px;
    font-size: 11px;
  }
}

.directory-list {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  width: 100%;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .directory-list {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .directory-list {
    padding: 8px;
  }
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 10px;
}

.list-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

@media (max-width: 768px) {
  .list-header h3 {
    font-size: 15px;
  }
}

@media (max-width: 480px) {
  .list-header h3 {
    font-size: 14px;
  }
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.refresh-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  background: #f0f0f0;
  color: #333;
  white-space: nowrap;
}

.refresh-btn:hover {
  background: #e0e0e0;
}

@media (max-width: 768px) {
  .refresh-btn {
    padding: 5px 10px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .refresh-btn {
    padding: 4px 8px;
    font-size: 11px;
  }
}

.log-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  background: #e6f7ff;
  color: #1890ff;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
}

.log-btn:hover {
  background: #bae7ff;
}

.log-icon {
  font-size: 14px;
}

@media (max-width: 768px) {
  .log-btn {
    padding: 5px 10px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .log-btn {
    padding: 4px 8px;
    font-size: 11px;
  }
}

.table-container {
  overflow-x: auto;
  border-radius: 6px;
  border: 1px solid #eee;
  width: 100%;
}

@media (max-width: 768px) {
  .table-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 800px;
}

@media (max-width: 768px) {
  table {
    min-width: 100%;
  }
}

/* 间距调整 */
th, td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #eee;
  line-height: 1.4;
}

@media (max-width: 768px) {
  th, td {
    padding: 6px 8px;
    font-size: 12px;
  }
}

th {
  background-color: #fafafa;
  font-weight: 500;
  color: #555;
  white-space: nowrap;
}

.user-row {
  background-color: #fff;
}

.directory-row {
  background-color: #f9f9f9;
}

.directory-row:hover {
  background-color: #f0f0f0;
}

.directory-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 28px;
  gap: 8px;
}

.directory-path {
  word-break: break-all;
  font-size: 12px;
}

@media (max-width: 768px) {
  .directory-path {
    font-size: 11px;
  }
}

.remove-btn {
  background: none;
  border: none;
  color: #ff4d4f;
  cursor: pointer;
  font-size: 16px;
  padding: 0 4px;
  opacity: 0.7;
  transition: opacity 0.2s;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.remove-btn:hover {
  opacity: 1;
  background: #fff2f0;
}

.add-directory-btn {
  padding: 4px 10px;
  background: #f0f7ff;
  color: #1890ff;
  border: 1px dashed #91d5ff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  white-space: nowrap;
}

.add-directory-btn:hover {
  background: #e6f7ff;
  border-color: #69c0ff;
}

@media (max-width: 768px) {
  .add-directory-btn {
    padding: 3px 8px;
    font-size: 11px;
  }
}

.select-dropdown {
  padding: 4px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  font-size: 12px;
  width: 100%;
  min-width: 60px;
  outline: none;
}

.select-dropdown:focus {
  border-color: #1890ff;
}

@media (max-width: 768px) {
  .select-dropdown {
    padding: 3px 6px;
    font-size: 11px;
    min-width: 50px;
  }
}

.quota {
  font-size: 11px;
  color: #666;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .quota {
    font-size: 10px;
  }
}

/* 待生效文本的特殊样式 */
.pending-text {
  color: #ff0000;
}

.actions {
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: nowrap;
}

@media (max-width: 768px) {
  .action-buttons {
    gap: 2px;
  }
}

.action-btn {
  padding: 4px 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .action-btn {
    padding: 3px 6px;
    font-size: 11px;
  }
}

.save-btn {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.save-btn:hover {
  background: #e6f7d7;
}

.deactivate {
  background: #fff2f0;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
}

.activate {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.action-btn:hover {
  opacity: 0.9;
}

/* 对话框样式 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
}

.directory-dialog {
  background: white;
  border-radius: 8px;
  width: 500px;
  max-width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

@media (max-width: 768px) {
  .directory-dialog {
    width: 100%;
    max-height: 95vh;
  }
}

.dialog-header {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fafafa;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .dialog-header {
    padding: 10px 12px;
  }
}

.dialog-header h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  flex-grow: 1;
  color: #333;
  word-break: break-all;
}

@media (max-width: 768px) {
  .dialog-header h3 {
    font-size: 14px;
  }
}

.current-path {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .current-path {
    max-width: 180px;
  }
}

.nav-btn {
  padding: 4px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  white-space: nowrap;
}

.nav-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
}

@media (max-width: 768px) {
  .nav-btn {
    padding: 3px 8px;
    font-size: 11px;
  }
}

.dialog-content {
  padding: 0;
  overflow-y: auto;
  flex-grow: 1;
  max-height: 400px;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  color: #999;
  font-size: 13px;
}

.directory-tree ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.directory-entry {
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  transition: background 0.2s;
  border-bottom: 1px solid #f5f5f5;
  font-size: 13px;
}

@media (max-width: 768px) {
  .directory-entry {
    padding: 8px 12px;
    font-size: 12px;
  }
}

.directory-entry:hover {
  background: #f5f5f5;
}

.directory-entry.selected {
  background: #e6f7ff;
}

.folder-icon {
  font-size: 16px;
  color: #ffc53d;
  flex-shrink: 0;
}

.directory-name {
  flex-grow: 1;
  word-break: break-all;
}

.enter-icon {
  color: #999;
  padding: 3px;
  border-radius: 4px;
  opacity: 0;
  transition: all 0.2s;
  font-size: 14px;
  cursor: pointer;
  flex-shrink: 0;
}

.directory-entry:hover .enter-icon {
  opacity: 1;
}

.enter-icon:hover {
  background: #e6f7ff;
  color: #1890ff;
}

.empty-directory {
  text-align: center;
  color: #999;
  padding: 30px 20px;
  font-size: 13px;
}

.dialog-actions {
  padding: 12px 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
  flex-wrap: wrap;
  gap: 10px;
}

@media (max-width: 768px) {
  .dialog-actions {
    padding: 10px 12px;
  }
}

.selected-path {
  font-size: 12px;
  color: #666;
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 10px;
}

@media (max-width: 768px) {
  .selected-path {
    font-size: 11px;
    max-width: 200px;
  }
}

.dialog-footer .action-buttons {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.dialog-btn {
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  min-width: 60px;
  border: none;
  transition: all 0.2s;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .dialog-btn {
    padding: 5px 10px;
    font-size: 11px;
    min-width: 50px;
  }
}

.dialog-btn.cancel {
  background: white;
  border: 1px solid #d9d9d9;
  color: #333;
}

.dialog-btn.cancel:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.dialog-btn.confirm {
  background: #1890ff;
  color: white;
}

.dialog-btn.confirm:disabled {
  background: #bae0ff;
  cursor: not-allowed;
}

.dialog-btn.confirm:not(:disabled):hover {
  background: #40a9ff;
}

.manual-input-section {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  background: #fafafa;
}

@media (max-width: 768px) {
  .manual-input-section {
    padding: 10px 12px;
  }
}

.input-header {
  margin-bottom: 8px;
}

.input-label {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

@media (max-width: 768px) {
  .input-label {
    font-size: 12px;
  }
}

.input-controls {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.directory-input {
  flex-grow: 1;
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s;
  min-width: 200px;
}

.directory-input:focus {
  border-color: #1890ff;
}

.directory-input::placeholder {
  color: #999;
}

@media (max-width: 768px) {
  .directory-input {
    padding: 5px 10px;
    font-size: 12px;
    min-width: 150px;
  }
}

@media (max-width: 480px) {
  .input-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .directory-input {
    width: 100%;
  }
  
  .manual-btn {
    width: 100%;
  }
}

.manual-btn {
  min-width: 60px;
  white-space: nowrap;
}

/* 移动端表格响应式 */
@media (max-width: 768px) {
  table,
  thead,
  tbody,
  th,
  td,
  tr {
    display: block;
  }
  
  thead {
    display: none;
  }
  
  tr {
    margin-bottom: 15px;
    border: 1px solid #eee;
    border-radius: 6px;
    background: #fff;
  }
  
  td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid #eee;
    text-align: right;
    position: relative;
  }
  
  td:before {
    content: attr(data-label);
    font-weight: 600;
    color: #666;
    padding-right: 10px;
    white-space: nowrap;
  }
  
  td:last-child {
    border-bottom: none;
  }
  
  .user-row td:first-child:before {
    content: "用户名";
  }
  
  .user-row td:nth-child(2):before {
    content: "状态";
  }
  
  .user-row td:nth-child(3):before {
    content: "目录";
  }
  
  .user-row td:nth-child(7):before {
    content: "操作";
  }
  
  .directory-row td:nth-child(3):before {
    content: "目录";
  }
  
  .directory-row td:nth-child(4):before {
    content: "只读";
  }
  
  .directory-row td:nth-child(5):before {
    content: "删除";
  }
  
  .directory-row td:nth-child(6):before {
    content: "空间";
  }
  
  .add-directory-btn {
    width: 100%;
    text-align: center;
  }
  
  .directory-item {
    width: 100%;
  }
  
  .select-dropdown {
    width: auto;
    min-width: 80px;
  }
  
  .actions {
    width: 100%;
  }
  
  .action-buttons {
    justify-content: flex-end;
    margin-right:20px;
  }
}

@media (max-width: 480px) {
  td {
    padding: 6px 10px;
    font-size: 12px;
  }
  
  .select-dropdown {
    min-width: 70px;
    padding: 2px 4px;
  }
  
  .action-btn {
    padding: 2px 6px;
    font-size: 11px;
  }
}

/* 日志对话框样式 */
.log-dialog :deep(.el-dialog__body) {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.log-toolbar .log-stats {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 13px;
  color: #606266;
}

.log-toolbar .log-stats .auto-refresh-control {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 16px;
  border-left: 1px solid #dcdfe6;
}

.log-toolbar .log-stats .auto-refresh-control .refresh-label {
  font-size: 12px;
  color: #909399;
}

.log-toolbar .log-stats .auto-refresh-control .refresh-interval {
  font-size: 12px;
  color: #67c23a;
}

.log-toolbar .log-actions {
  display: flex;
  gap: 8px;
}

@media (max-width: 768px) {
  .log-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .log-toolbar .log-stats {
    width: 100%;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .log-toolbar .log-stats .auto-refresh-control {
    padding-left: 0;
    border-left: none;
  }
  
  .log-toolbar .log-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 480px) {
  .log-toolbar .log-stats {
    font-size: 12px;
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.log-content .empty-log {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
}

.log-content .empty-log .el-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.log-content .empty-log p {
  margin: 0;
  font-size: 14px;
}

.log-content .log-list {
  padding: 12px;
}

.log-content .log-list .log-item {
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.log-content .log-list .log-item:last-child {
  border-bottom: none;
}

.log-content .log-list .log-item.log-level-error {
  background-color: #fef0f0;
}

.log-content .log-list .log-item.log-level-warn {
  background-color: #fdf6ec;
}

.log-content .log-list .log-item .log-time {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-content .log-list .log-item .log-level {
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-content .log-list .log-item .log-level.level-error {
  color: #f56c6c;
}

.log-content .log-list .log-item .log-level.level-warn {
  color: #e6a23c;
}

.log-content .log-list .log-item .log-level.level-info {
  color: #409eff;
}

.log-content .log-list .log-item .log-message {
  color: #303133;
  word-break: break-all;
  flex: 1;
}

@media (max-width: 768px) {
  .log-content {
    max-height: 350px;
  }
  
  .log-content .log-list {
    padding: 8px;
  }
  
  .log-content .log-list .log-item {
    font-size: 12px;
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .log-content .log-list .log-item .log-time {
    width: 100%;
    margin-bottom: 2px;
  }
  
  .log-content .log-list .log-item .log-message {
    width: 100%;
    margin-left: 0;
  }
}

@media (max-width: 480px) {
  .log-content {
    max-height: 300px;
  }
  
  .log-content .log-list .log-item {
    font-size: 11px;
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .log-pagination {
    justify-content: center;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

@media (max-width: 480px) {
  .dialog-footer {
    flex-direction: column;
  }
  
  .dialog-footer .el-button {
    width: 100%;
    margin-left: 0 !important;
  }
}

/* Element Plus 对话框移动端适配 */
.settings-dialog :deep(.el-dialog) {
  @media (max-width: 768px) {
    width: 95% !important;
    margin-top: 5vh !important;
  }
  
  @media (max-width: 480px) {
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    max-height: 100%;
    border-radius: 0;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin-bottom: 0 !important;
  }
  
  @media (max-width: 480px) {
    .el-dialog__header {
      padding: 12px 16px;
      border-bottom: 1px solid #ebeef5;
    }
    
    .el-dialog__body {
      padding: 16px;
      max-height: calc(80vh - 120px);
      overflow-y: auto;
    }
    
    .el-dialog__footer {
      padding: 12px 16px;
      border-top: 1px solid #ebeef5;
    }
  }
}

/* Element Plus 组件适配 */
:deep(.el-input__inner) {
  @media (max-width: 768px) {
    font-size: 13px;
    height: 32px;
    line-height: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    height: 30px;
    line-height: 30px;
  }
}

:deep(.el-select) {
  @media (max-width: 768px) {
    .el-input__inner {
      height: 32px;
      line-height: 32px;
    }
  }
  
  @media (max-width: 480px) {
    .el-input__inner {
      height: 30px;
      line-height: 30px;
    }
  }
}

:deep(.el-button--small) {
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 11px;
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
    }

    .el-switch__core:after {
      width: 14px !important;
      height: 14px !important;
    }
  }
}

/* 服务卡片可点击样式 */
.service-card.clickable {
  cursor: pointer;
}

.service-card.clickable:hover {
  background: #f8f9fa;
}

.service-hint {
  margin: 5px 0 0 0;
  color: #999;
  font-size: 13px;
}

/* 弹窗内容样式 */
.online-edit-content {
  padding: 10px 0;
}

.settings-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.settings-table th,
.settings-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ebeef5;
}

.settings-table th {
  background-color: #f5f7fa;
  color: #909399;
  font-weight: 600;
  white-space: nowrap;
}

.settings-table td {
  color: #606266;
  vertical-align: middle;
}

.settings-table .full-width {
  width: 100%;
}

.action-cell {
  white-space: nowrap;
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
}
</style>